import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';
import { CaretDown, CaretUp, X } from 'phosphor-react';

export type TimelineStepState = 'done' | 'pending' | 'error';

export interface TimelineStep {
  label: string;
  state: TimelineStepState;
  at?: Date | string | null;
  detail?: string | null;
  // Expandable list of times under the step, e.g. each time an email was viewed.
  times?: (Date | string)[];
}

export interface DeliveryTimelinePanelProps {
  title: string;
  subtitle?: string;
  facts: { label: string; value: React.ReactNode }[];
  steps: TimelineStep[];
  onClose: () => void;
}

const formatTime = (value?: Date | string | null) =>
  value ? moment(value).format('MM/DD/YYYY, h:mm:ss A') : '';

const DOT_CLASSES: Record<TimelineStepState, string> = {
  done: 'bg-green-700 border-green-700',
  pending: 'bg-white border-gray-300',
  error: 'bg-red-600 border-red-600',
};

const TimelineStepRow = ({ step, isLast }: { step: TimelineStep; isLast: boolean }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const times = step.times ?? [];
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <span
          className={twMerge('mt-0.5 h-3.5 w-3.5 rounded-full border-2', DOT_CLASSES[step.state])}
        />
        {!isLast && (
          <span
            className={twMerge(
              'w-0.5 flex-1 min-h-[28px]',
              step.state === 'pending' ? 'bg-gray-200' : 'bg-gray-400'
            )}
          />
        )}
      </div>
      <div className="pb-5 min-w-0">
        <button
          type="button"
          disabled={times.length === 0}
          onClick={() => setIsExpanded((prev) => !prev)}
          className={twMerge(
            'flex items-center gap-1 bg-transparent border-none p-0 text-sm font-semibold text-left',
            step.state === 'pending' ? 'text-gray-400' : 'text-gray-900',
            step.state === 'error' && 'text-red-700',
            times.length > 0 ? 'cursor-pointer' : 'cursor-default'
          )}>
          {step.label}
          {times.length > 0 && (isExpanded ? <CaretUp size={12} /> : <CaretDown size={12} />)}
        </button>
        {step.at && (
          <p className="text-xs font-medium text-gray-700 mt-0.5">{formatTime(step.at)}</p>
        )}
        {step.detail && (
          <p
            className={twMerge(
              'text-xs mt-0.5 break-words',
              step.state === 'error' ? 'text-red-600' : 'text-gray-500'
            )}>
            {step.detail}
          </p>
        )}
        {isExpanded && (
          <div className="mt-1 flex flex-col gap-0.5">
            {times.map((time, index) => (
              <p key={index} className="text-xs font-medium text-gray-700">
                {formatTime(time)}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/** Side panel with one delivery's details and its step-by-step timeline. */
export const DeliveryTimelinePanel = ({
  title,
  subtitle,
  facts,
  steps,
  onClose,
}: DeliveryTimelinePanelProps) =>
  createPortal(
    <div className="fixed inset-0 z-[9999] flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative h-full w-[380px] max-w-full bg-white shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="min-w-0">
            <p className="text-base font-semibold text-gray-900 truncate">{title}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="bg-transparent border-none p-1 cursor-pointer text-gray-500 hover:text-gray-800">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-4 flex flex-col gap-3 border-b border-gray-100">
            {facts.map((fact) => (
              <div key={fact.label}>
                <p className="text-xs font-semibold text-gray-900">{fact.label}</p>
                <div className="text-sm text-gray-700 break-words">{fact.value || '—'}</div>
              </div>
            ))}
          </div>
          <div className="px-5 py-4">
            <p className="text-sm font-semibold text-gray-900 mb-3">Delivery activity</p>
            {steps.map((step, index) => (
              <TimelineStepRow
                key={`${step.label}_${index}`}
                step={step}
                isLast={index === steps.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
